<template>
  <v-layout class="gap-3" column>
    <v-layout class="gap-3">
      <v-flex
        :xs5="isAnyInfosRule"
        :xs4="!isAnyInfosRule"
      >
        <v-layout class="gap-3">
          <v-flex
            :xs4="!isObjectRule && isAnyInfosRule"
            :xs6="isObjectRule"
          >
            <pattern-attribute-field
              :value="rule.attribute"
              :items="attributes"
              :name="name"
              :disabled="disabled || disabledField"
              return-object
              required
              @input="updateAttribute"
            />
          </v-flex>
          <v-flex
            v-if="isAnyInfosRule"
            xs8
          >
            <c-infos-attribute-field
              v-field="rule"
              :items="infos"
              :name="name"
              :disabled="disabled"
              :combobox="isInfosRule"
              row
            />
          </v-flex>
          <v-flex
            v-else-if="isObjectRule"
            xs6
          >
            <v-text-field
              v-field="rule.dictionary"
              v-validate="'required'"
              :name="objectDictionaryName"
              :disabled="disabled"
              :label="$t('common.dictionary')"
              :error-messages="errors.collect(objectDictionaryName)"
            />
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex
        v-if="rule.attribute"
        :xs8="!isAnyInfosRuleOrAlias"
        :xs7="isAnyInfosRuleOrAlias"
      >
        <v-layout class="gap-3">
          <v-flex
            v-if="isInfosValueField"
            xs1
          >
            <c-input-type-field
              v-field="rule.fieldType"
              :label="$t('common.type')"
              :types="inputTypesWithDefinedType"
              :disabled="disabled"
              :name="name"
            />
          </v-flex>
          <v-flex
            v-if="shownOperatorField"
            v-bind="operatorFlexSizeAttrs"
          >
            <pattern-operator-field
              v-field="rule.operator"
              :operators="operators"
              :disabled="disabled || disabledOperator"
              :name="operatorFieldName"
              required
            />
          </v-flex>
          <v-flex
            v-if="rule.operator && operatorHasValue"
            v-bind="valueFlexSizeAttrs"
          >
            <component
              v-bind="valueComponent.props"
              :is="valueComponent.is"
              v-on="valueComponent.on"
            />
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout>
      <c-alert
        v-if="notDefinedType && !disabled"
        type="warning"
      >
        {{ $t('pattern.notDefinedType') }}
      </c-alert>
    </v-layout>
  </v-layout>
</template>

<script>
import { isFunction } from 'lodash';

import {
  PATTERN_FIELD_TYPES,
  PATTERN_RULE_INFOS_FIELDS,
  PATTERN_RULE_TYPES,
  DEFAULT_PATTERN_FIELD_TYPES,
} from '@/constants';

import {
  getFieldType,
  isDateRuleType,
  isDurationRuleType,
  isExtraInfosRuleType,
  isInfosRuleType,
  isObjectRuleType,
  isOperatorHasValue,
  isNumberRuleType,
} from '@/helpers/entities/pattern/form';

import { formMixin } from '@/mixins/form';

import DateTimePickerTextField from '@/components/forms/fields/date-time-picker/date-time-picker-text-field.vue';

import PatternAttributeField from './pattern-attribute-field.vue';
import PatternOperatorField from './pattern-operator-field.vue';
import PatternRuleFieldDateValue from './pattern-rule-field-date-value.vue';

export default {
  inject: ['$validator'],
  components: {
    DateTimePickerTextField,
    PatternAttributeField,
    PatternOperatorField,
    PatternRuleFieldDateValue,
  },
  mixins: [formMixin],
  model: {
    prop: 'rule',
    event: 'input',
  },
  props: {
    rule: {
      type: Object,
      required: true,
    },
    attributes: {
      type: Array,
      default: () => [],
    },
    infos: {
      type: Array,
      default: () => [],
    },
    operators: {
      type: Array,
      default: () => [],
    },
    inputTypes: {
      type: Array,
      default: () => DEFAULT_PATTERN_FIELD_TYPES,
    },
    valueField: {
      type: Object,
      required: false,
    },
    type: {
      type: String,
      default: PATTERN_RULE_TYPES.string,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledField: {
      type: Boolean,
      default: false,
    },
    disabledOperator: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'rule',
    },
    itemDisabled: {
      type: Function,
      required: false,
    },
  },
  computed: {
    operatorFieldName() {
      return `${this.name}.operator`;
    },

    valueFieldName() {
      return `${this.name}.value`;
    },

    inputType() {
      return getFieldType(this.rule.value);
    },

    isInfosRule() {
      return isInfosRuleType(this.type);
    },

    isExtraInfosRule() {
      return isExtraInfosRuleType(this.type);
    },

    isObjectRule() {
      return isObjectRuleType(this.type);
    },

    isAnyInfosRule() {
      return this.isInfosRule || this.isExtraInfosRule;
    },

    isAnyInfosRuleOrAlias() {
      return this.isAnyInfosRule || this.rule.alias;
    },

    isInfosValueField() {
      return this.rule.field === PATTERN_RULE_INFOS_FIELDS.value || this.rule.alias;
    },

    isDateRule() {
      return isDateRuleType(this.type) || this.rule.fieldType === PATTERN_FIELD_TYPES.timestamp;
    },

    isDurationRule() {
      return isDurationRuleType(this.type);
    },

    isNumberRule() {
      return isNumberRuleType(this.type);
    },

    inputTypesWithDefinedType() {
      const item = this.isInfosRule
        ? this.infos.find(info => info.value === this.rule.dictionary)
        : this.attributes.find(attribute => attribute.value === this.rule.attribute);

      if (!item?.definedType) {
        return this.inputTypes;
      }

      return this.inputTypes.map(type => ({
        ...type,
        defined: type.value === item.definedType,
      }));
    },

    valueComponent() {
      const valueProps = {
        value: this.rule.value,
        required: true,
        disabled: this.disabled,
        name: this.valueFieldName,
        label: this.$t('common.value'),
      };

      const valueHandlers = {
        input: this.updateValue,
      };

      if (this.isDateRule) {
        return {
          is: 'pattern-rule-field-date-value',
          props: {
            value: this.rule.range,
            operator: this.rule.operator,
            disabled: this.disabled,
            name: this.valueFieldName,
          },
          on: {
            input: this.updateRange,
          },
        };
      }

      if (this.valueField) {
        const valueFieldProps = isFunction(this.valueField.props)
          ? this.valueField.props.call(this, this.rule)
          : this.valueField.props;

        const valueFieldOn = isFunction(this.valueField.on)
          ? this.valueField.on.call(this, this.rule)
          : this.valueField.on;

        return {
          is: this.valueField.is,
          props: {
            disabled: this.disabled,

            ...valueProps,
            ...valueFieldProps,
          },
          on: {
            ...valueHandlers,
            ...valueFieldOn,
          },
        };
      }

      if (this.isDurationRule) {
        return {
          is: 'c-duration-field',
          props: {
            duration: this.rule.duration,
            disabled: this.disabled,
            name: this.valueFieldName,
            medium: true,
          },
          on: {
            input: this.updateDuration,
          },
        };
      }

      if (this.isNumberRule) {
        return {
          is: 'c-number-field',
          props: valueProps,
          on: valueHandlers,
        };
      }

      return {
        is: 'c-mixed-input-field',
        props: {
          inputType: this.inputType,
          types: this.inputTypes,
          ...valueProps,
        },
        on: valueHandlers,
      };
    },

    operatorHasValue() {
      const hasValue = isOperatorHasValue(this.rule.operator);

      if (this.isAnyInfosRule) {
        return this.isInfosValueField && hasValue;
      }

      return hasValue;
    },

    shownOperatorField() {
      return this.operators.length > 0 || this.operators[0] !== this.rule.operator;
    },

    objectDictionaryName() {
      return `${this.name}.dictionary`;
    },

    operatorFlexSizeAttrs() {
      if (this.isDateRule) {
        return { xs5: true };
      }

      if (this.isAnyInfosRule) {
        return { xs4: true };
      }

      return { xs6: true };
    },

    valueFlexSizeAttrs() {
      if (this.isAnyInfosRule || this.isDateRule) {
        return { xs7: true };
      }

      return { xs6: true };
    },

    definedType() {
      return this.inputTypesWithDefinedType.find(type => type.defined);
    },

    notDefinedType() {
      return this.rule.fieldType && this.definedType && this.definedType.value !== this.rule.fieldType;
    },
  },
  methods: {
    preparedItemDisabled(item) {
      return this.itemDisabled?.(this.rule, item);
    },

    updateAttribute(attribute) {
      const newRule = {
        ...this.rule,

        attribute: attribute?.value,
        alias: attribute?.alias ?? false,
      };

      if (attribute?.definedType) {
        newRule.fieldType = attribute.definedType;
      }

      this.updateModel(newRule);
    },

    updateDuration(duration) {
      this.updateField('duration', duration);
    },

    updateRange(duration) {
      this.updateField('range', duration);
    },

    updateValue(value) {
      this.updateField('value', value);
    },
  },
};
</script>
